import { useEffect, useRef, useState, type ComponentType } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  Gauge,
  DatabaseBackup,
  Archive,
  Briefcase,
  Building2,
  FolderOpen,
  Calendar,
  CalendarCheck,
  ChevronDown,
  ChevronRight,
  HandHelping,
  HardDrive,
  Home,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Mail,
  MapPin,
  Music,
  MessagesSquare,
  Receipt,
  Scale,
  ScrollText,
  Shield,
  ShieldCheck,
  Filter,
  Send,
  Tent,
  Server,
  Settings,
  ShoppingBag,
  ShoppingCart,
  User,
  BadgeCheck,
  Banknote,
  BookOpen,
  BookUser,
  Shirt,
  Users,
  UsersRound,
  Truck,
  Wallet,
  Vault as VaultIcone,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';
import { fetchBoutiqueCommandesEnAttente } from '../services/api';
import BadgeEspace from './BadgeEspace';
import BadgeGratuite from './BadgeGratuite';
import BadgeReset from './BadgeReset';
import BadgeParrainage from './BadgeParrainage';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

interface Lien {
  to: string;
  icone: ComponentType<{ className?: string }>;
  label: string;
  /**
   * Un nombre a traiter, pose en pastille sur l'entree.
   *
   * Reserve a ce qui attend une action et qu'on ne verra pas autrement : des
   * commandes a preparer. Une pastille sur ce qui se consulte simplement
   * apprendrait a l'ignorer, et elle ne servirait plus nulle part.
   */
  badge?: number;
}

/**
 * Le site public : ce que voit un visiteur, et ce vers quoi un membre revient.
 *
 * Ces trois entrées restent à plat, hors de toute section. Les replier
 * n'économiserait rien — elles tiennent en trois lignes — et cacherait
 * l'accueil derrière un clic.
 */
const liensPublics: Lien[] = [
  { to: '/', icone: Home, label: 'Accueil' },
  { to: '/evenements', icone: Calendar, label: 'Événements' },
  { to: '/association', icone: Landmark, label: 'Association' },
];

/**
 * Navigation latérale.
 *
 * Elle empilait jusqu'à seize entrées d'affilée, sans hiérarchie : le site
 * public, l'espace du bénévole, la gestion du bureau et la comptabilité s'y
 * suivaient au même niveau, et deux d'entre elles s'appelaient « Événements ».
 * Retrouver « Archives » demandait de lire toute la liste.
 *
 * Les entrées internes sont désormais groupées par ce qu'on vient y faire, et
 * chaque groupe se replie. La section qui contient la page ouverte se déplie
 * d'elle-même : arriver sur un écran sans voir où l'on est dans le menu est le
 * défaut que le repli aurait pu introduire.
 *
 * Ce qu'une fonction au bureau n'a pas le droit de voir ne paraît pas, et une
 * section dont toutes les entrées sont ainsi retirées disparaît avec elles :
 * un titre qui n'ouvre sur rien est pire qu'une entrée de trop.
 */
export default function DesktopNav() {
  const { user, logout, aLeDroit } = useAuth();
  const perso = usePersonnalisation();
  // Les options de la plateforme effacent leurs entrées quand elles ne sont
  // pas incluses ; tant qu'elles ne sont pas lues, on n'efface rien.
  const optionOuverte = (nom: string) => (perso?.options as any)?.[nom] !== false;
  const isAdmin = user?.roles?.includes('ROLE_ADMIN') === true;
  const isSuperAdmin = user?.roles?.includes('ROLE_SUPER_ADMIN') === true;
  const isBureau = user?.roles?.includes('ROLE_BUREAU') === true || isAdmin;
  const isBenevole = user?.roles?.includes('ROLE_VOLUNTEER') === true || isBureau;

  // Les commandes qui attendent, pour la pastille du menu. Relevé à
  // l'ouverture puis toutes les cinq minutes : une commande de goodies n'est
  // pas une urgence, et le menu est sur toutes les pages.
  const commandesEnAttente = useQuery({
    queryKey: ['boutique-commandes-en-attente'],
    queryFn: fetchBoutiqueCommandesEnAttente,
    enabled: aLeDroit('voir_boutique'),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });
  const aTraiter = commandesEnAttente.data?.aTraiter ?? 0;

  // Rien de tout cela pour un visiteur : il n'a ni espace, ni fonction. Le
  // menu se réduit alors au site public, à l'appel à parrainage et au bouton
  // de connexion du pied — c'est tout ce dont il peut se servir.

  // Deux entrées directes, hors sections : l'accès rapide, porte d'entrée du
  // quotidien, et le coffre — on vient y chercher un mot de passe bien plus
  // souvent qu'on n'ouvre une section. Le coffre suit l'option de la
  // plateforme : souscrite, ou ouverte d'office en essai et pendant une
  // gratuité.
  const liensDirects: Lien[] = user === null || user === undefined ? [] : [
    ...(isBenevole ? [{ to: '/acces-rapide', icone: LayoutGrid, label: 'Accès rapide' }] : []),
    ...(aLeDroit('voir_vault') && optionOuverte('vault') ? [{ to: '/admin/vault', icone: VaultIcone, label: 'Coffre-fort' }] : []),
  ];

  const sections: { titre: string; icone: ComponentType<{ className?: string }>; liens: Lien[] }[] = (user === null || user === undefined ? [] : [
    {
      titre: 'Mon espace',
      icone: User,
      liens: [
        { to: '/informations', icone: User, label: 'Informations' },
        // L'espace d'une famille : sa cotisation, ses pieces, son equipe.
        // Ouvert a tout compte — la page se tait d'elle-meme si rien ne le
        // concerne, plutot que de faire dependre le menu d'une requete.
        ...(optionOuverte('licences') ? [{ to: '/mon-adhesion', icone: BadgeCheck, label: 'Mon adhésion' }] : []),
        ...(isBenevole ? [{ to: '/mes-missions', icone: HandHelping, label: 'Missions' }] : []),
        // Les boites de fonction ont leur entree : elles se consultent
        // plusieurs fois par semaine, et les laisser au bas de « Mon espace »
        // obligeait a defiler pour un geste quotidien.
        ...(isBenevole ? [{ to: '/messagerie', icone: Mail, label: 'Messagerie' }] : []),
        ...(aLeDroit('voir_reunions') ? [{ to: '/bureau/reunions', icone: MessagesSquare, label: 'Réunions' }] : []),
        // Les guides sont dans « Mon espace » et non en administration : on
        // les ouvre quand on ne sait pas faire quelque chose, pas quand on
        // règle le logiciel. Chacun n'y voit que ce qui le concerne.
        ...(isBenevole ? [{ to: '/guides', icone: BookOpen, label: 'Guides' }] : []),
      ],
    },
    // La bibliotheque tient dans la section Bureau : c'est du secretariat, pas
    // de la comptabilite ni de l'administration du site.
    {
      titre: 'Bureau',
      icone: Briefcase,
      liens: [
        // Même libellé que l'entrée publique du haut, et c'est voulu : ce sont
        // les mêmes festivités, vues du côté de qui les organise. Le titre de
        // section les sépare mieux qu'un synonyme approximatif ne le ferait.
        ...(aLeDroit('voir_evenements') ? [{ to: '/bureau/evenements', icone: CalendarCheck, label: 'Événements' }] : []),
        ...(aLeDroit('voir_benevoles') ? [{ to: '/bureau/membres', icone: Users, label: 'Membres' }] : []),
        // La page unifiee : repertoire, devis, factures et avoirs sponsor.
        ...(aLeDroit('voir_sponsors') || aLeDroit('voir_facturation')
          ? [{ to: '/bureau/facturation', icone: Building2, label: 'Sponsors' }]
          : []),
        ...(aLeDroit('voir_documents')
          ? [{ to: '/bureau/documents', icone: FolderOpen, label: 'Documents' }]
          : []),
        // Ecrire au groupe : c'est ce que fait un secretaire quand il
        // convoque, d'ou sa place ici plutot qu'en administration.
        ...(aLeDroit('diffuser_messages')
          ? [{ to: '/bureau/diffusion', icone: Send, label: 'Diffusion' }]
          : []),
        // Ecrire a une selection : le meme droit, un autre geste. Cinq listes
        // toutes faites d'un cote ; de l'autre, « ceux qui doivent encore
        // quelque chose pour la saison en cours », que le tresorier tirait
        // jusqu'ici dans un tableur. La selection ciblee appartient au module
        // Festival : sans lui, l'entree n'existe pas.
        ...(aLeDroit('diffuser_messages') && optionOuverte('festival')
          ? [{ to: '/bureau/campagnes', icone: Filter, label: 'Sélection ciblée' }]
          : []),
      ],
    },
    // Le festival a sa SECTION, pas une entree dans « Bureau » : quatre ecrans
    // qui se frequentent tous les jours pendant la preparation meritent d'etre
    // a un clic chacun, pas derriere un onglet interne. La section suit
    // l'option ET le droit — le droit seul montrerait un module non souscrit,
    // l'option seule l'ouvrirait a tout le bureau — et disparait entiere
    // quand ils manquent, comme les autres.
    ...(aLeDroit('voir_festival') && optionOuverte('festival')
      ? [{
          titre: 'Festival',
          icone: Tent,
          liens: [
            { to: '/bureau/festival/programmation', icone: CalendarDays, label: 'Programmation' },
            { to: '/bureau/festival/zones', icone: MapPin, label: 'Zones' },
            { to: '/bureau/festival/artistes', icone: Music, label: 'Artistes' },
            // Les cachets ne paraissent qu'a qui porte leur droit : l'entree
            // est absente, ni grisee ni masquee — on ne dessine pas une porte
            // que le droit n'ouvre pas.
            ...(aLeDroit('voir_festival_contrats')
              ? [{ to: '/bureau/festival/contrats', icone: Wallet, label: 'Contrats' }]
              : []),
          ],
        }]
      : []),
    // Les assurances : meme regime que le festival — une section a part,
    // sous l'option ET le droit, qui disparait entiere quand ils manquent.
    ...(aLeDroit('voir_assurance') && optionOuverte('assurance')
      ? [{
          titre: 'Assurance',
          icone: Shield,
          liens: [
            { to: '/bureau/assurance/contrats', icone: ScrollText, label: 'Contrats' },
            { to: '/bureau/assurance/attestations', icone: Receipt, label: 'Attestations' },
            { to: '/bureau/assurance/sinistres', icone: ShieldCheck, label: 'Sinistres' },
            { to: '/bureau/assurance/biens', icone: HardDrive, label: 'Biens assurés' },
          ],
        }]
      : []),
    // Le repertoire : quatre populations, une seule question — « quel est son
    // numero ? ». Elle se posait de la meme facon partout et se cherchait dans
    // quatre ecrans batis pour autre chose. Chaque volet garde le droit de
    // l'ecran dont il tire ses donnees : le repertoire raccourcit un chemin,
    // il n'en ouvre pas de nouveau.
    ...(isBenevole
      ? [{
          titre: 'Répertoire',
          icone: BookUser,
          liens: [
            { to: '/repertoire/association', icone: Users, label: 'Association' },
            // Le registre suit son option : sans gestion des adherents, le
            // volet n'aurait personne a montrer.
            ...(aLeDroit('voir_adherents') && optionOuverte('licences')
              ? [{ to: '/repertoire/adherents', icone: UsersRound, label: 'Adhérents' }]
              : []),
            ...(aLeDroit('suivre_achats')
              ? [{ to: '/repertoire/fournisseurs', icone: Truck, label: 'Fournisseurs' }]
              : []),
            ...(aLeDroit('voir_sponsors')
              ? [{ to: '/repertoire/sponsors', icone: Building2, label: 'Sponsors' }]
              : []),
          ],
        }]
      : []),
    // Le registre des adherents : une autre population que les benevoles du
    // bureau, et assez d'ecrans pour meriter sa section. Le president y donne
    // acces a ses entraineurs ; le tresorier y vient pour les cotisations.
    ...(aLeDroit('voir_adherents') && optionOuverte('licences')
      ? [{
          titre: 'Adhérents',
          icone: UsersRound,
          liens: [
            /*
              LE TABLEAU EN PREMIER, ET C'EST TOUT L'OBJET.

              Les cinq écrans du registre ne disaient pas par où commencer : il
              fallait les visiter l'un après l'autre pour savoir s'il y avait
              quelque chose à faire. Celui-ci répond d'un coup, et mène à
              l'écran où l'on agit.
            */
            { to: '/adherents/tableau-de-bord', icone: Gauge, label: 'Où en est le club' },
            { to: '/adherents', icone: BookUser, label: 'Répertoire' },
            { to: '/adherents/cotisations', icone: Wallet, label: 'Licences et cotisations' },
            { to: '/adherents/equipements', icone: Shirt, label: 'Équipements' },
            { to: '/adherents/equipes', icone: Users, label: 'Équipes' },
            // Le calendrier vient apres les equipes : on compose un groupe
            // avant de savoir contre qui il joue, et l'ordre des liens suit
            // l'ordre dans lequel un entraineur s'en sert.
            { to: '/adherents/rencontres', icone: CalendarDays, label: 'Calendrier' },
          ],
        }]
      : []),
    {
      titre: 'Comptabilité',
      icone: Receipt,
      liens: [
        // Le bilan en tete : la question qu'on vient poser a la comptabilite
        // est « ou en est-on ? » — ventes contre achats, annee et festivites.
        ...(aLeDroit('voir_tableau_bord') ? [{ to: '/bureau/bilan', icone: Scale, label: 'Bilan' }] : []),
        // Les commandes de la boutique : une recette encaissée et un colis à
        // faire partir. Sa pastille dit combien attendent — c'est la seule
        // chose de cet écran qui ne peut pas attendre la semaine prochaine.
        ...(aLeDroit('voir_boutique')
          ? [{ to: '/bureau/boutique', icone: ShoppingBag, label: 'Boutique', badge: aTraiter }]
          : []),
        // Les virements recus et leur rapprochement : c'est de la
        // tresorerie, pas de l'administration.
        ...(aLeDroit('voir_facturation')
          ? [{ to: '/bureau/encaissements', icone: Banknote, label: 'Encaissements' }]
          : []),
        ...(aLeDroit('voir_tableau_bord') ? [{ to: '/bureau/tableau-de-bord', icone: LayoutDashboard, label: 'Suivi ventes' }] : []),
        // Le suivi des achats reste consultable même option fermée : c'est
        // l'écriture qui se verrouille, pas l'historique du trésorier.
        ...(aLeDroit('suivre_achats') ? [{ to: '/bureau/achats', icone: ShoppingCart, label: 'Suivi achats' }] : []),
        ...(aLeDroit('voir_archives') ? [{ to: '/bureau/archives', icone: Archive, label: 'Archives' }] : []),
      ],
    },
    {
      titre: 'Administration',
      icone: Settings,
      liens: [
        // Le catalogue se gere en administration : c'est un reglage, tenu une
        // fois par saison. L'ecran existe des que le droit est la — preparer
        // le catalogue avant de souscrire est un usage voulu.
        ...(aLeDroit('voir_boutique') ? [{ to: '/admin/boutique', icone: ShoppingBag, label: 'Boutique' }] : []),
        ...(isAdmin ? [{ to: '/admin/parametres', icone: Settings, label: 'Paramètres' }] : []),
        // Les droits ont leur ecran, juste apres les parametres : c'est la
        // qu'on vient quand quelqu'un dit « je n'ai pas acces a ». Ils
        // vivaient jusqu'ici a trois endroits differents, dont un ou l'on ne
        // pouvait pas les regler du tout.
        ...(isAdmin ? [{ to: '/admin/droits', icone: ShieldCheck, label: 'Gestion des droits' }] : []),
        // Les reglages du registre : ouverts au tresorier et a l'entraineur,
        // qui ne sont pas administrateurs du site pour autant.
        ...(aLeDroit('configurer_adherents') && optionOuverte('licences')
          ? [{ to: '/admin/adherents', icone: UsersRound, label: 'Adhérents' }]
          : []),
        // Les sauvegardes et la place occupee : ce qu'un president verifie
        // rarement de lui-meme, et qu'il doit pouvoir trouver sans appeler.
        ...(isAdmin ? [{ to: '/admin/sauvegardes', icone: DatabaseBackup, label: 'Sauvegardes' }] : []),
        // A cote des sauvegardes, et c'est le bon voisinage : on y vient
        // quand le quota se remplit, c'est-a-dire quand on regardait deja
        // combien de place il reste.
        ...(aLeDroit('gerer_fichiers')
          ? [{ to: '/admin/fichiers', icone: HardDrive, label: 'Fichiers déposés' }]
          : []),
        // Reserve aux administrateurs, et ce n'est pas un oubli : ouvert a
        // tout le bureau, le journal deviendrait un outil de surveillance
        // entre benevoles, ce qu'il n'est pas.
        ...(isAdmin ? [{ to: '/admin/journal', icone: ScrollText, label: 'Journal des actions' }] : []),
        // Les durees de conservation et l'effacement a la demande : deux
        // faces d'une meme obligation, sur un meme ecran.
        ...(isAdmin ? [{ to: '/admin/donnees', icone: ShieldCheck, label: 'Données personnelles' }] : []),
        // Reservee a l'exploitant de la plateforme : le client ne la voit pas.
        ...(isSuperAdmin ? [{ to: '/admin/devops', icone: Server, label: 'Support technique' }] : []),
      ],
    },
  ]).filter((section) => section.liens.length > 0);

  /*
   * Une seule section ouverte à la fois.
   *
   * Chacune gardait son propre état, de sorte qu'on pouvait les déplier
   * toutes : le menu dépassait alors la hauteur de l'écran, il fallait le
   * faire défiler pour atteindre le bas, et le repli perdait sa raison d'être.
   *
   * Celle qui contient la page affichée s'ouvre d'elle-même, et se referme
   * quand on en ouvre une autre : arriver sur un écran sans voir où l'on est
   * dans le menu est le défaut que le repli aurait pu introduire.
   */
  const { pathname } = useLocation();
  const sectionDeLaPage =
    sections.find((s) => s.liens.some((lien) => pathname.startsWith(lien.to.split('?')[0])))?.titre ?? null;
  const [sectionOuverte, setSectionOuverte] = useState<string | null>(sectionDeLaPage);

  /*
   * L'ouverture d'office a lieu une fois par page visitée, et pas une de plus.
   *
   * Ne dépendre que de la section de la page ne suffirait pas : cette valeur
   * est nulle tant que l'identité n'est pas revenue, puis prend son vrai nom.
   * L'effet doit donc se déclencher à ce moment-là — mais une seule fois, sinon
   * il rouvrirait la section de la page sous les doigts de qui vient d'en
   * ouvrir une autre.
    */
  const cheminDejaOuvert = useRef<string | null>(null);

  useEffect(() => {
    if (sectionDeLaPage !== null && cheminDejaOuvert.current !== pathname) {
      cheminDejaOuvert.current = pathname;
      setSectionOuverte(sectionDeLaPage);
    }
  }, [pathname, sectionDeLaPage]);

  return (
    <aside className="hidden lg:flex flex-col w-64 h-screen sticky top-0 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 shadow-sm z-40">
      <div className="px-6 py-5 border-b border-gray-50 dark:border-gray-800">
        <Link to="/" className="block">
          <Logo size="md" />
        </Link>
        {/* La période offerte, sous le nom : elle concerne l'association,
            sa place est là, pas en travers de l'écran. */}
        <span className="flex flex-wrap items-center gap-1.5 mt-2">
          <BadgeGratuite />
          <BadgeReset />
          <BadgeEspace />
          <BadgeParrainage />
        </span>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
        {liensPublics.map((lien) => (
          <EntreeMenu key={lien.to} lien={lien} />
        ))}

        {(liensDirects.length > 0 || sections.length > 0) && (
          <div className="pt-4 mt-4 border-t border-gray-100 dark:border-gray-800 space-y-1">
            {liensDirects.map((lien) => (
              <EntreeMenu key={lien.to} lien={lien} />
            ))}
            {sections.map((section) => (
                  <SectionMenu
                    key={section.titre}
                    titre={section.titre}
                    icone={section.icone}
                    liens={section.liens}
                    ouverte={sectionOuverte === section.titre}
                    surBascule={() =>
                      setSectionOuverte(sectionOuverte === section.titre ? null : section.titre)
                    }
                  />
            ))}
          </div>
        )}

        {/* L'appel à parrainage ne s'adresse pas à ceux qui tiennent
            l'association : le bureau y verrait un bouton sans objet. */}
        {!isBureau && (
          <div className="pt-4 mt-4 border-t border-gray-100 dark:border-gray-800">
            <Link
              to="/sponsors"
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
            >
              <Building2 className="w-4 h-4" />
              Devenir sponsor
            </Link>
          </div>
        )}
      </nav>

      {/* Pied : apparence et compte connecté */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Apparence</span>
          <ThemeToggle />
        </div>
        {user ? (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-bold overflow-hidden shrink-0">
              {user.photoUrl ? (
                <img
                  src={user.photoUrl}
                  alt={user.fullName || user.email}
                  className="w-full h-full object-cover"
                />
              ) : (
                user.fullName?.charAt(0) || user.email.charAt(0)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate dark:text-gray-100">{user.fullName || user.email}</p>
              <button
                onClick={() => logout()}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1 mt-0.5"
              >
                <LogOut className="w-3 h-3" />
                Se déconnecter
              </button>
            </div>
          </div>
        ) : (
          <NavLink
            to="/login"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <User className="w-5 h-5" />
            <span>Se connecter</span>
          </NavLink>
        )}
      </div>
    </aside>
  );
}

/**
 * Une entrée du menu, au premier niveau ou dans une section.
 */
function EntreeMenu({ lien, indente = false }: { lien: Lien; indente?: boolean }) {
  const Icone = lien.icone;

  return (
    <NavLink
      to={lien.to}
      end={lien.to === '/' || lien.to === '/sponsors'}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl transition-colors ${
          indente ? 'pl-6 pr-4 py-2.5 text-sm' : 'px-4 py-3'
        } ${
          isActive
            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
        }`
      }
    >
      <Icone className="w-5 h-5 shrink-0" />
      <span className="truncate">{lien.label}</span>
      {lien.badge !== undefined && lien.badge > 0 && (
        <span
          className="ml-auto shrink-0 min-w-[1.25rem] rounded-full bg-primary-600 px-1.5 py-0.5 text-center text-[0.7rem] font-semibold leading-none text-white tabular-nums"
          title={`${lien.badge} commande${lien.badge > 1 ? 's' : ''} à traiter`}
        >
          {lien.badge > 99 ? '99+' : lien.badge}
        </span>
      )}
    </NavLink>
  );
}

/**
 * Un groupe d'entrées, replié ou déplié.
 *
 * Une seule section reste ouverte à la fois, et c'est le menu qui en décide :
 * l'état vit chez lui, pas ici. Chacune gardant le sien, on pouvait toutes les
 * ouvrir — le menu dépassait alors la hauteur de l'écran, et le repli ne
 * servait plus à rien.
 */
function SectionMenu({
  titre,
  icone: Icone,
  liens,
  ouverte,
  surBascule,
}: {
  titre: string;
  icone: ComponentType<{ className?: string }>;
  liens: Lien[];
  ouverte: boolean;
  surBascule: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={surBascule}
        aria-expanded={ouverte}
        className="w-full flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <Icone className="w-4 h-4 shrink-0" />
        <span className="flex-1 text-left truncate">{titre}</span>
        {ouverte ? (
          <ChevronDown className="w-4 h-4 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 shrink-0" />
        )}
      </button>

      {ouverte && (
        <div className="mt-0.5 space-y-0.5">
          {liens.map((lien) => (
            <EntreeMenu key={lien.to} lien={lien} indente />
          ))}
        </div>
      )}
    </div>
  );
}
